import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { Result } from 'antd'
import { VehicleRoute } from '../../Components'
import { GET } from '../../utils/apis'
import { secureApi } from '../../Config/api'
import { elmecLogo, englishFlag, italyFlag } from '../../assets'

const REFRESH_MS = 30000
// A failed poll retries sooner than the regular cadence, so a transient
// backend hiccup (proxy timeout, DB latency) costs seconds, not half a minute.
const RETRY_MS = 8000
const FEED_TIMEZONE = 'Europe/Rome'

const TEXT = {
    it: {
        unavailable: 'Servizio momentaneamente non raggiungibile, nuovo tentativo in corso…',
        notFound: 'Dispositivo non trovato',
        missing: 'ID dispositivo mancante nell\'URL',
        offline: 'AGGIORNAMENTO NON RIUSCITO',
        lastUpdate: 'ultimo aggiornamento',
        subtitle: 'Orario live del veicolo',
    },
    en: {
        unavailable: 'Service temporarily unreachable, retrying…',
        notFound: 'Device not found',
        missing: 'Device ID is missing in the URL',
        offline: 'UPDATE FAILED',
        lastUpdate: 'last update',
        subtitle: 'Live vehicle schedule',
    },
}

const formatFeedTime = (date, language) => {
    const locale = language === 'it' ? 'it-IT' : 'en-GB'
    try {
        return date.toLocaleTimeString(locale, { timeZone: FEED_TIMEZONE })
    } catch (e) {
        return date.toLocaleTimeString(locale)
    }
}

// Clock in the feed's timezone, driven by the SERVER's time: a signage
// player with a wrong local clock still shows Italy time. offsetMs is
// server-now minus browser-now, measured on every successful poll.
// Isolated so the 1 Hz tick doesn't re-render the whole schedule panel.
const LiveClock = ({ language, offsetMs }) => {
    const [now, setNow] = useState(() => new Date())
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000)
        return () => clearInterval(id)
    }, [])
    const shown = new Date(now.getTime() + (offsetMs || 0))
    return <span className='rf-clock'>{formatFeedTime(shown, language)}</span>
}

// Signage players (Xibo's webpage widget) rewrite or append to the query
// string, so a bare "?E157" can arrive as "?E157&preview=1", "?E157=" or
// url-encoded. Resolve the device from every shape we've seen:
//   /route-forecast/E157          (path - safest for Xibo, like /parking-slots/:id)
//   /route-forecast?device=E157   (named key: device | id | serial | sn)
//   /route-forecast?E157          (bare key, possibly with extra params after it)
const DEVICE_KEYS = ['device', 'id', 'serial', 'sn']
const resolveDevice = (pathDevice, search) => {
    const clean = (v) => {
        try { v = decodeURIComponent(v || '') } catch (e) { v = v || '' }
        return String(v).trim().replace(/\/+$/, '')
    }
    if (clean(pathDevice)) return clean(pathDevice)
    const raw = (search || '').replace(/^\?/, '')
    if (!raw) return ''
    if (!raw.includes('=')) return clean(raw.split('&')[0])
    const params = new URLSearchParams(raw)
    for (const key of DEVICE_KEYS) {
        if (clean(params.get(key))) return clean(params.get(key))
    }
    // Bare key with an empty value (e.g. "?E157&foo=1", "?E157=")
    for (const [key, value] of params.entries()) {
        if (!value && clean(key)) return clean(key)
    }
    return ''
}

// Display language: the URL decides first (?lang=it|en — a signage player
// never gets clicked), then the viewer's last choice on this browser, then
// Italian. Independent of the logged-in app's language: this page is public.
const LANG_KEY = 'rf-language'
const LANGUAGES = ['it', 'en']
const initialLanguage = (search) => {
    const fromUrl = (new URLSearchParams((search || '').replace(/^\?/, '')).get('lang') || '').toLowerCase()
    if (LANGUAGES.includes(fromUrl)) return fromUrl
    try {
        const saved = localStorage.getItem(LANG_KEY)
        if (LANGUAGES.includes(saved)) return saved
    } catch (e) { /* storage blocked (private mode, sandboxed iframe) */ }
    return 'it'
}

// Backend answers that mean "this URL will never work" — everything else
// (network error, proxy timeout, 5xx, decode failure) is transient and the
// last good panel stays on screen while we retry.
const isDefinitiveFailure = (res) =>
    res?.success === false && ['Device not found', 'device is required'].includes(res?.message)

// Public, no-login view of one vehicle's live route/schedule - the same panel
// the maps screen shows, addressable by device ID (ud_sn) or serial number.
const RouteForecast = () => {
    const location = useLocation()
    const params = useParams()
    const [language, setLanguage] = useState(() => initialLanguage(location.search))
    const t = TEXT[language] || TEXT.it
    const changeLanguage = (lang) => {
        if (!LANGUAGES.includes(lang)) return
        setLanguage(lang)
        try { localStorage.setItem(LANG_KEY, lang) } catch (e) { /* see initialLanguage */ }
    }

    const device = resolveDevice(params?.device, location.search)

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [data, setData] = useState(null)
    // Last poll failed but an older panel is still on screen.
    const [stale, setStale] = useState(false)
    const [lastUpdate, setLastUpdate] = useState(null)
    const [clockOffsetMs, setClockOffsetMs] = useState(0)
    const dataRef = useRef(null)

    useEffect(() => {
        if (!device) {
            setError(`${t.missing} (${window.location.pathname}${window.location.search || ''}). /route-forecast/E157`)
            setLoading(false)
            return
        }

        let cancelled = false
        let timer = null

        const schedule = (ms) => {
            if (cancelled) return
            clearTimeout(timer)
            timer = setTimeout(() => load(true), ms)
        }

        const load = async (silent) => {
            if (!silent) setLoading(true)
            let res = null
            try {
                // secureApi decodes the backend's JWT-wrapped response body —
                // plain axios would hand us the token string, not the payload.
                res = await secureApi.get(`${GET?.ROUTE_FORECAST}?device=${encodeURIComponent(device)}`)
            } catch (err) {
                res = null
            }
            if (cancelled) return

            if (res?.success) {
                dataRef.current = res
                setData(res)
                setError(null)
                setStale(false)
                setLastUpdate(new Date())
                const serverMs = Date.parse(res?.server_time?.iso)
                if (Number.isFinite(serverMs)) setClockOffsetMs(serverMs - Date.now())
                setLoading(false)
                schedule(REFRESH_MS)
                return
            }

            if (isDefinitiveFailure(res)) {
                dataRef.current = null
                setData(null)
                setStale(false)
                setError(res.message === 'device is required' ? t.missing : t.notFound)
                setLoading(false)
                // The device may get attached to a vehicle later — keep polling.
                schedule(REFRESH_MS)
                return
            }

            // Transient: keep whatever is on screen, flag it, retry soon.
            if (dataRef.current) {
                setStale(true)
            } else {
                setError(t.unavailable)
            }
            setLoading(false)
            schedule(RETRY_MS)
        }

        load(false)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [device, language])

    let body
    if (loading && !data) {
        body = (
            <div className='route-forecast-panel'>
                <VehicleRoute
                    language={language}
                    vehicle={null}
                    schedule={null}
                    vehicleStops={{ past: [], current: null, future: [] }}
                    past={[]}
                    current={null}
                    future={[]}
                    loading={true}
                />
            </div>
        )
    } else if (data) {
        body = (
            <div className='route-forecast-panel'>
                <VehicleRoute
                    language={language}
                    vehicle={data.vehicle}
                    schedule={data}
                    vehicleStops={{ past: [], current: null, future: [] }}
                    past={[]}
                    current={null}
                    future={[]}
                    loading={false}
                />
            </div>
        )
    } else if (error) {
        body = <Result status={error === t.unavailable ? 'warning' : '404'} title='Route Forecast' subTitle={error} />
    }

    const replay = !!data?.server_time?.replay

    return (
        <div className='rf-page'>
            <header className='rf-header'>
                <img src={elmecLogo} alt='Elmec Elettronica' className='rf-logo' />
                <span className='rf-divider' />
                <div className='rf-title-block'>
                    <span className='rf-title'>Route Forecast</span>
                    <span className='rf-subtitle'>{t.subtitle}</span>
                </div>
                <div className='rf-header-right'>
                    {replay && (
                        <span className='rf-replay' title={`Server feed clock: ${data?.server_time?.feed_date} ${data?.server_time?.feed_time}`}>
                            REPLAY {data?.server_time?.feed_time}
                        </span>
                    )}
                    {data && !stale && (
                        <span className='rf-live'>
                            <span className='rf-live-dot' />
                            LIVE
                        </span>
                    )}
                    {data && stale && (
                        <span className='rf-stale'>
                            {t.offline}
                            {lastUpdate && ` · ${t.lastUpdate} ${formatFeedTime(new Date(lastUpdate.getTime() + clockOffsetMs), language)}`}
                        </span>
                    )}
                    <div className='rf-lang' role='group' aria-label='Language'>
                        <button
                            type='button'
                            className={`rf-lang-btn${language === 'it' ? ' is-active' : ''}`}
                            onClick={() => changeLanguage('it')}
                            title='Italiano'
                        >
                            <img src={italyFlag} alt='Italiano' />
                        </button>
                        <button
                            type='button'
                            className={`rf-lang-btn${language === 'en' ? ' is-active' : ''}`}
                            onClick={() => changeLanguage('en')}
                            title='English'
                        >
                            <img src={englishFlag} alt='English' />
                        </button>
                    </div>
                    <span className='rf-device-badge'>{data?.vehicle?.ud_sn || device}</span>
                    <LiveClock language={language} offsetMs={clockOffsetMs} />
                </div>
            </header>
            <main className='rf-main'>{body}</main>
        </div>
    )
}

export default RouteForecast
